import React from 'react';
import { Box, Skeleton, Menu, MenuItem } from '@mui/material';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { DownChevron } from 'src/components/SvgIcons/SvgIcons';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import IconIncreaseSvg from '!svg-react-loader!src/components/BeaconV2Components/icons/assets/icon-increase.svg';
import IconDecreaseSvg from '!svg-react-loader!src/components/BeaconV2Components/icons/assets/icon-decrease.svg';
import { ContentProfileModal } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Library/ContentProfileModal';
import AppProfileIcon from 'src/components/BeaconV2Components/Header/AppProfileIcon';
import colors from 'src/utils/colors';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import { useBeaconClientId, useCompanyId } from 'src/utils/Hooks/reduxSelectors';
import { getClientImageUrl } from 'src/utils/image';
import { buildShortName } from 'src/utils/stringFormatting';

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: {
    absolute: string;
    percentage: string;
  };
  isLoading?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, change, isLoading = false }) => {
  // Determine if change is zero, positive, or negative
  const absoluteValue = change ? parseFloat(change.absolute) : 0;
  const isZero = change && absoluteValue === 0;
  const isPositive = change && absoluteValue > 0;

  // Determine the color based on change direction
  const getChangeColor = () => {
    if (isZero) return colors.blueGrey;
    if (isPositive) return colors.brandClubGreen;
    return colors.redBright;
  };

  // Show skeleton if loading or if value is not available
  const showSkeleton = isLoading || value === undefined || value === null || value === '';

  return (
    <Box
      sx={{
        backgroundColor: colors.contentGenBackground,
        borderRadius: '4px',
        padding: '12px 12px 8px 12px',
        width: '242px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      }}
    >
      <Text
        sx={{
          color: colors.grey, // Grays/Primary from Figma
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '20px',
          letterSpacing: '0.96px',
          textTransform: 'uppercase'
        }}
      >
        {title}
      </Text>
      <Flex alignItems="center" gap={1}>
        {showSkeleton ? (
          <Skeleton variant="text" width={80} height={32} />
        ) : (
          <>
            <Text
              sx={{
                color: colors.darkBlue, // Primary dark blue from Figma
                fontSize: '20px',
                fontWeight: 400,
                lineHeight: '32px'
              }}
            >
              {value}
            </Text>
            {change && (
              <Flex alignItems="center" sx={{ gap: '4px', marginLeft: '8px'}}>
                {!isZero && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '6px',
                      height: '4px',
                      '& svg': {
                        width: '6px',
                        height: '4px',
                        fill: getChangeColor()
                      }
                    }}
                  >
                    {isPositive ? <IconIncreaseSvg /> : <IconDecreaseSvg />}
                  </Box>
                )}
                <Text
                  sx={{
                    color: getChangeColor(),
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '18px'
                  }}
                >
                  ({change.percentage})
                </Text>
              </Flex>
            )}
          </>
        )}
      </Flex>
    </Box>
  );
};

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
  maxWidth?: string;
  onClick?: () => void;
  interactive?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, maxWidth, onClick, interactive = false }) => {
  return (
    <Flex alignItems="flex-start" gap={0.5}>
      <Text
        sx={{
          color: colors.grey, // Grays/Primary from Figma
          fontSize: '16px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          lineHeight: '24px',
          width: '56px', // Fixed width for all labels to align values
          flexShrink: 0,
          letterSpacing: '0.08px',
          marginRight: '4px'
        }}
      >
        {label}
      </Text>
      <Text
        sx={{
          color: colors.darkBlue, // Primary dark blue from Figma
          fontSize: '16px',
          fontWeight: 400,
          lineHeight: '24px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: maxWidth || '354px',
          cursor: interactive ? 'pointer' : 'default',
          '&:hover': interactive
            ? {
                textDecoration: 'underline'
              }
            : {}
        }}
        onClick={onClick}
      >
        {value}
      </Text>
    </Flex>
  );
};

interface VersionComparisonCardProps {
  versionName: string;
  details: {
    source?: string | null;
    fileName?: string | null;
    project?: string | null;
    profile?: string | null;
    profileId?: string | null;
    date?: string | null;
    status: string | null;
  };
  detailsLoading?: boolean;
  detailsVariant?: 'current' | 'master' | 'generated';
  metrics: {
    contentUtilization: string | number;
    keywordsUsed: number;
    productiveKeywords: number;
    keywordPopularity: string | number;
  };
  changes?: {
    contentUtilization?: { absolute: string; percentage: string };
    keywordsUsed?: { absolute: string; percentage: string };
    productiveKeywords?: { absolute: string; percentage: string };
    keywordPopularity?: { absolute: string; percentage: string };
  };
  versions?: Array<{
    id: string;
    label: string; // e.g., "Version 02"
    updatedAt?: string; // ISO string
    /**
     * Accepts both object and legacy string (userId) forms
     */
    updatedBy?:
      | {
          userId?: string;
          displayName?: string;
          role?: string;
          imageSrc?: string;
        }
      | string;
    /**
     * Creator metadata; accepts both object and legacy string (userId) forms
     */
    createdBy: {
      userId: string;
      displayName: string;
      role: string;
      imageSrc: string;
    };
  }>;
  onVersionChange?: (versionId: string) => void;
  onSetAsMaster?: () => void;
  showSetAsMaster?: boolean;
  showChevron?: boolean;
  metricLoadingStates?: {
    utilization?: boolean;
    keywordUsage?: boolean;
    productivity?: boolean;
    popularity?: boolean;
  };
  isLoading?: boolean;
  isVersionsLoading?: boolean;
}

export const VersionComparisonCard: React.FC<VersionComparisonCardProps> = ({
  versionName,
  details,
  detailsLoading = false,
  detailsVariant,
  metrics,
  changes,
  versions,
  onVersionChange,
  onSetAsMaster,
  showSetAsMaster = true,
  showChevron = false,
  metricLoadingStates = {},
  isLoading = false,
  isVersionsLoading = false
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const mainEntityShort = useAppSelector((state) => state.entityService.mainEntity.shortDisplayName);
  const beaconClientId = useBeaconClientId();
  const companyId = useCompanyId();
  const clientImageUrl = getClientImageUrl(beaconClientId === -1 ? companyId : beaconClientId);
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (versions && versions.length > 0) {
      setAnchorEl(event.currentTarget as unknown as HTMLElement);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const selectedVersionId = versions?.find((v) => v.label === versionName)?.id ?? null;

  const formatUpdatedAt = (iso?: string, id?: string) => {
    const raw = iso || details.date;
    if (!raw) return '';
    const d = new Date(raw);
    const formatted = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const isUpdated = id === 'current' || id === 'master';
    return `${isUpdated ? 'Last Updated' : 'Last Generated'} on ${formatted}`;
  };

  // Prefer explicit prop when supplied; otherwise derive from versionName and library status
  const derivedDisabledFromVersionName = versionName === 'Current Content' || versionName === 'Uploaded Content';
  const isSetAsMasterDisabled =
    derivedDisabledFromVersionName || (details?.status || '').toLowerCase() === 'master';

  return (
    <Box
      sx={{
        width: '500px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        position: 'relative',
        flexShrink: 0
      }}
    >
      {/* Version dropdown trigger */}
      <Flex
        alignItems="center"
        gap={1}
        sx={{
          cursor: versions && versions.length > 0 ? 'pointer' : 'default'
        }}
        onClick={handleClick}
      >
        {isVersionsLoading || !versionName ? (
          <Skeleton variant="text" width={180} height={28} />
        ) : (
          <Text
            sx={{
              color: colors.darkBlue,
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {versionName}
          </Text>
        )}
        {(showChevron || (versions && versions.length > 0)) && (
          <DownChevron
            width={24}
            height={24}
            style={{
              color: colors.darkBlue
            }}
          />
        )}
      </Flex>

      {/* Custom dropdown menu */}
      {versions && versions.length > 0 && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          sx={{
            borderRadius: '8px',
            width: '420px',
            '& .MuiPaper-root': {
              width: '420px',
              maxWidth: 'unset',
              boxSizing: 'border-box',
              border: `1px solid ${colors.graysSecondary}`,
              maxHeight: '576px',
              overflowY: 'auto'
            }
          }}
          MenuListProps={{
            sx: { paddingY: 0 }
          }}
        >
          {isVersionsLoading
            ? Array.from({ length: 4 }).map((_, idx) => (
                <MenuItem key={`skeleton-${idx}`} sx={{ padding: 0, width: '420px', minHeight: '72px' }}>
                  <Flex alignItems="center" justifyContent="space-between" sx={{ width: '100%', p: '8px 16px 6px 12px' }}>
                    <Flex flexDirection="column" sx={{ overflow: 'hidden', width: '100%' }}>
                      <Skeleton variant="text" width={180} height={20} />
                      <Skeleton variant="text" width={220} height={18} />
                    </Flex>
                  </Flex>
                </MenuItem>
              ))
            : versions.map((version) => {
            const isSelected = version.id === selectedVersionId;
            const isCurrentOrMaster = version.id === 'current' || version.id === 'master';
            // Use company name for current/master entries, but user's display name for generated versions
            const name = isCurrentOrMaster ? mainEntityShort : (version.createdBy.displayName || mainEntityShort);
            const roleRaw = version.createdBy.role;
            const role = roleRaw ? `${roleRaw.charAt(0).toUpperCase()}${roleRaw.slice(1)}` : 'Support';
            const userId = version.createdBy.userId;
            const imageSrc = version.createdBy.imageSrc;
            const initials = buildShortName(name) || 'SL';
            const resolvedImageSrc = isCurrentOrMaster ? clientImageUrl : imageSrc;

            return (
              <MenuItem
                key={version.id}
                onClick={() => {
                  onVersionChange?.(version.id);
                  handleClose();
                }}
                sx={{
                  padding: 0,
                  width: '420px',
                  minHeight: '72px',
                  '&:not(:last-of-type)': { borderBottom: `1px solid ${colors.graysSecondary}` },
                  backgroundColor: isSelected ? 'rgba(67, 132, 245, 0.12)' : 'transparent',
                  '&:hover': {
                    backgroundColor: isSelected ? 'rgba(67, 132, 245, 0.18)' : colors.contentGenBackground
                  },
                  '&.Mui-selected': {
                    backgroundColor: isSelected ? 'rgba(67, 132, 245, 0.12)' : 'transparent'
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: isSelected ? 'rgba(67, 132, 245, 0.12)' : 'transparent'
                  }
                }}
              >
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%', padding: '8px 16px 6px 12px' }}
                >
                  {/* Left - version title and date */}
                  <Flex flexDirection="column" sx={{ overflow: 'hidden' }}>
                    <Text
                      sx={{
                        color: colors.darkBlue,
                        fontSize: '14px',
                        fontWeight: 500,
                        lineHeight: '20px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {version.label}
                    </Text>
                    <Text
                      sx={{
                        color: colors.darkBlue,
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '20px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {formatUpdatedAt(version.updatedAt, version.id)}
                    </Text>
                  </Flex>

                  {/* Right - user info */}
                  <Flex alignItems="center" sx={{ gap: '8px', flexShrink: 0 }}>
                    <Flex flexDirection="column" sx={{ textAlign: 'right' }}>
                      <Text
                        sx={{
                          color: colors.darkBlue,
                          fontSize: '12px',
                          fontWeight: 500,
                          lineHeight: '18px',
                          maxWidth: '220px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {name}
                      </Text>
                      <Text
                        sx={{
                          fontSize: '12px',
                          fontWeight: 400,
                          lineHeight: '18px'
                        }}
                      >
                        {role}
                      </Text>
                    </Flex>
                    <AppProfileIcon size={30} userId={userId} imageSrc={resolvedImageSrc} initials={initials} fontSize="14px" />
                  </Flex>
                </Flex>
              </MenuItem>
            );
          })}
        </Menu>
      )}

      {/* Details section */}
      <Flex flexDirection="column" gap={0.5} sx={{ '& > *': { marginBottom: '4px' }, '& > *:last-child': { marginBottom: 0 } }}>
        {/* Current/Master variant: Source, Date, Status */}
        {(detailsVariant === 'current' || detailsVariant === 'master') && (
          <>
            <DetailRow
              label="Source:"
              value={
                detailsLoading || !details.source ? (
                  <Skeleton variant="text" width={160} height={24} />
                ) : (
                  details.source
                )
              }
            />
            <DetailRow
              label="Date:"
              value={
                detailsLoading || !details.date ? (
                  <Skeleton variant="text" width={120} height={24} />
                ) : (
                  details.date
                )
              }
            />
            <DetailRow
              label="Status:"
              value={
                detailsLoading || !details.status ? (
                  <Skeleton variant="text" width={100} height={24} />
                ) : (
                  details.status.replace(/_/g, ' ')
                )
              }
            />
          </>
        )}
        {/* Generated variant: Project, Profile, Status */}
        {detailsVariant === 'generated' && (
          <>
            <DetailRow
              label="Project:"
              value={
                detailsLoading || !details.project ? (
                  <Skeleton variant="text" width={180} height={24} />
                ) : (
                  details.project
                )
              }
            />
            <DetailRow
              label="Profile:"
              value={
                detailsLoading || !details.profile ? (
                  <Skeleton variant="text" width={200} height={24} />
                ) : details.profileId ? (
                  <ContentProfileModal profileId={details.profileId}>
                    {details.profile}
                  </ContentProfileModal>
                ) : (
                  details.profile
                )
              }
              maxWidth="435px"
              interactive={!!details.profileId && !!details.profile}
            />
            <DetailRow
              label="Status:"
              value={
                detailsLoading || !details.status ? (
                  <Skeleton variant="text" width={100} height={24} />
                ) : (
                  details.status.replace(/_/g, ' ')
                )
              }
            />
          </>
        )}
        {/* Fallback when no variant specified - render available fields with skeleton fallback */}
        {!detailsVariant && (
          <>
            {(details.source !== undefined || detailsLoading) && (
              <DetailRow
                label="Source:"
                value={
                  detailsLoading || !details.source ? (
                    <Skeleton variant="text" width={160} height={24} />
                  ) : (
                    details.source
                  )
                }
              />
            )}
            {(details.project !== undefined || detailsLoading) && (
              <DetailRow
                label="Project:"
                value={
                  detailsLoading || !details.project ? (
                    <Skeleton variant="text" width={180} height={24} />
                  ) : (
                    details.project
                  )
                }
              />
            )}
            {(details.date !== undefined || detailsLoading) && (
              <DetailRow
                label="Date:"
                value={
                  detailsLoading || !details.date ? (
                    <Skeleton variant="text" width={120} height={24} />
                  ) : (
                    details.date
                  )
                }
              />
            )}
            {(details.profile !== undefined || detailsLoading) && (
              <DetailRow
                label="Profile:"
                value={
                  detailsLoading || !details.profile ? (
                    <Skeleton variant="text" width={200} height={24} />
                  ) : details.profileId ? (
                    <ContentProfileModal profileId={details.profileId}>
                      {details.profile}
                    </ContentProfileModal>
                  ) : (
                    details.profile
                  )
                }
                maxWidth="435px"
                interactive={!!details.profileId && !!details.profile}
              />
            )}
            <DetailRow
              label="Status:"
              value={
                detailsLoading || !details.status ? (
                  <Skeleton variant="text" width={100} height={24} />
                ) : (
                  details.status.replace(/_/g, ' ')
                )
              }
            />
          </>
        )}
      </Flex>

      {/* Key metrics */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          width: '100%'
        }}
      >
        <MetricCard
          title="CONTENT UTILIZATION"
          value={metrics.contentUtilization}
          change={changes?.contentUtilization}
          isLoading={metricLoadingStates.utilization}
        />
        <MetricCard
          title="KEYWORDS USED"
          value={metrics.keywordsUsed}
          change={changes?.keywordsUsed}
          isLoading={metricLoadingStates.keywordUsage}
        />
        <MetricCard
          title="PRODUCTIVE KEYWORDS"
          value={metrics.productiveKeywords}
          change={changes?.productiveKeywords}
          isLoading={metricLoadingStates.productivity}
        />
        <MetricCard
          title="KEYWORD POPULARITY"
          value={metrics.keywordPopularity}
          change={changes?.keywordPopularity}
          isLoading={metricLoadingStates.popularity}
        />
      </Box>

      {/* Set as master button */}
      {showSetAsMaster && onSetAsMaster && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0
          }}
        >
          <SlButton
            variant="normal"
            onClick={onSetAsMaster}
            disabled={isSetAsMasterDisabled}
            sx={{
              height: '32px',
              px: '20px',
              border: `1px solid ${isSetAsMasterDisabled ? colors.lightestGrey2 : colors.graysSecondary}`
            }}
          >
            <Text
              sx={{ color: isSetAsMasterDisabled ? colors.grey : colors.darkBlue, fontSize: '14px', fontWeight: 500 }}
            >
              Set as master
            </Text>
          </SlButton>
        </Box>
      )}
    </Box>
  );
};
